/**
 * "iCloud Contacts" source of the Address Book / Select Recipients modal: read-only contacts from an
 * Apple iCloud account over CardDAV, using an Apple app-specific password (Phase 1).
 * Guide: Sendlinx_iCloud_Contacts_Developer_Guide_Updated.docx §4 and §15.
 *
 * - Disconnected: Apple Account email + app-specific password (masked, show/hide), "Connect iCloud
 *   Account", how-to card with a real "Open Apple Account" link and the privacy notes.
 * - Reconnect: shown when Apple stopped accepting the saved app-specific password (revoked/changed).
 * - Connected: account, Last synced, Sync now, Disconnect, then search + contacts like Google Contacts.
 * The password lives only in this component's state until it is sent (HTTPS) and is cleared right
 * after; it is never put in browser storage. Rows feed the modal's shared selection ({ email, name }).
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Sync from "@material-ui/icons/Sync";
import CloudOutlined from "@material-ui/icons/CloudOutlined";
import AccountBoxOutlined from "@material-ui/icons/AccountBoxOutlined";
import LinkIcon from "@material-ui/icons/Link";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import EmojiObjectsOutlined from "@material-ui/icons/EmojiObjectsOutlined";
import OpenInNew from "@material-ui/icons/OpenInNew";
import ICloudContactsApi, { APPLE_ACCOUNT_URL, APP_PASSWORD_PATTERN } from "../../../../assets/js/utils/contacts/icloudContactsApi";

const PAGE = 50;
const AUTO_SYNC_AFTER_MS = 60 * 60 * 1000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const lower = (v) => String(v || "").toLowerCase();
const initials = (c) => {
    const src = (c.name || c.email || "?").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
    return ((src[0] || "?")[0] + (src.length > 1 ? src[src.length - 1][0] : "")).toUpperCase();
};
const formatWhen = (iso) => {
    if(!iso){ return ""; }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const messageOf = (err, fallback) => (err && err.message && err.message !== "Request Cancelled" ? err.message : fallback);
const SYNC_ERRORS = {
    throttled: "iCloud asked Sendlinx to slow down. The next sync will try again.",
    forbidden: "iCloud did not allow Sendlinx to read your contacts.",
    network: "iCloud could not be reached during the last sync.",
    too_large: "Your iCloud address book is too large to import.",
    truncated: "Only the first 25,000 iCloud contacts were imported."
};
const HELP_STEPS = [
    "Sign in to your Apple Account.",
    "Open Sign-In and Security.",
    "Select App-Specific Passwords.",
    "Generate a new password named “Sendlinx”.",
    "Paste it into the app-specific password field above."
];

function ICloudContactsView({ classes, isSelected, toggle, toggleMany, already }){
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [busy, setBusy] = useState(false);               // connecting / disconnecting
    const [syncing, setSyncing] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [notice, setNotice] = useState(null);            // { kind: "ok"|"error"|"info", text }
    const [appleId, setAppleId] = useState("");
    const [appPassword, setAppPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ key: "name", dir: 1 });
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({ total: 0, page: 0, hasMore: false, loading: false });
    const [listError, setListError] = useState(null);
    const requestId = useRef(0);
    const cancel = useRef(null);
    const mounted = useRef(true);
    const autoSynced = useRef(false);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const connected = !!(status && status.connected);

    const applyFailure = (err) => {
        const reason = err && err.reason;
        if(reason === "reconnect" || reason === "not_connected"){
            setStatus(s => ({ ...(s || {}), configured: true, connected: false, status: reason === "reconnect" ? "reconnect" : "disconnected" }));
            setRows(null);
            return true;
        }
        return false;
    };

    const loadStatus = () => {
        setStatusError(null);
        return ICloudContactsApi.status().then(data => {
            if(mounted.current){
                setStatus(data);
                if(data && data.account && data.account.email){ setAppleId(prev => prev || data.account.email); }
            }
            return data;
        }).catch(err => {
            if(mounted.current){ setStatusError(messageOf(err, "Your iCloud connection status could not be loaded.")); setStatus(s => s || { configured: true, connected: false, status: "disconnected" }); }
            return null;
        });
    };

    const loadContacts = (page = 1) => {
        const id = ++requestId.current;
        if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        cancel.current = source;
        setListError(null);
        setMeta(m => ({ ...m, loading: true }));
        return ICloudContactsApi.contacts({ q: search.trim(), sort: sort.key, dir: sort.dir === 1 ? "asc" : "desc", page, limit: PAGE }, source).then(data => {
            if(id !== requestId.current || !mounted.current){ return; }
            const items = data.items || [];
            setRows(prev => (page === 1 || !prev ? items : prev.concat(items.filter(r => !prev.some(p => p.id === r.id)))));
            setMeta({ total: data.total || 0, page, hasMore: !!data.hasMore, loading: false });
        }).catch(err => {
            if(id !== requestId.current || !mounted.current || (err && err.message === "Request Cancelled")){ return; }
            setMeta(m => ({ ...m, loading: false }));
            if(!applyFailure(err)){
                setRows(prev => prev || []);
                setListError(messageOf(err, "Your iCloud contacts could not be loaded."));
            }
        });
    };

    const runSync = (quiet = false) => {
        if(syncing){ return Promise.resolve(); }
        setSyncing(true);
        if(!quiet){ setNotice(null); }
        return ICloudContactsApi.sync(false).then(data => {
            if(!mounted.current){ return; }
            setSyncing(false);
            setStatus(data);
            const result = data.result || {};
            if(!quiet){
                setNotice(result.alreadyRunning ? { kind: "info", text: "A sync is already running. The list updates when it finishes." }
                    : { kind: "ok", text: "iCloud contacts synced." });
            }
            loadContacts(1);
        }).catch(err => {
            if(!mounted.current){ return; }
            setSyncing(false);
            if(applyFailure(err)){
                setNotice({ kind: "error", text: messageOf(err, "Apple no longer accepts the saved app-specific password.") });
                loadStatus();
                return;
            }
            if(!quiet){ setNotice({ kind: "error", text: messageOf(err, "Your iCloud contacts could not be synced.") }); }
            loadStatus();
        });
    };

    useEffect(() => {
        mounted.current = true;
        loadStatus();
        return () => {
            mounted.current = false;
            if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!connected){ setRows(null); return; }
        if(!autoSynced.current){
            autoSynced.current = true;
            const last = status && status.lastSyncedAt ? new Date(status.lastSyncedAt).getTime() : 0;
            const running = status && status.sync && status.sync.status === "running";
            if(!running && (!last || Date.now() - last > AUTO_SYNC_AFTER_MS)){ runSync(true); }
        }
    }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!connected){ return undefined; }
        const timer = setTimeout(() => loadContacts(1), rows === null ? 0 : 300);
        return () => clearTimeout(timer);
    }, [connected, search, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    const validate = () => {
        const errors = {};
        if(!EMAIL_PATTERN.test(appleId.trim())){
            errors.apple_account_email = "Enter the email address you use to sign in to your Apple Account.";
        }
        if(!APP_PASSWORD_PATTERN.test(appPassword.replace(/\s+/g, ""))){
            errors.app_specific_password = "Enter an app-specific password in the format xxxx-xxxx-xxxx-xxxx. Your main Apple Account password won't work here.";
        }
        return errors;
    };

    const connect = (e) => {
        if(e){ e.preventDefault(); }
        if(busy){ return; }
        setNotice(null);
        const errors = validate();
        setFieldErrors(errors);
        if(Object.keys(errors).length > 0){
            const first = errors.apple_account_email ? emailRef.current : passwordRef.current;
            if(first){ first.focus(); }
            return;
        }
        setBusy(true);
        ICloudContactsApi.connect(appleId.trim(), appPassword).then(data => {
            if(!mounted.current){ return; }
            setBusy(false);
            setAppPassword("");
            setShowPassword(false);
            setFieldErrors({});
            autoSynced.current = true;
            const syncError = data && data.syncError;
            setNotice(syncError ? { kind: "info", text: "iCloud account connected, but the first import didn't finish: "+syncError.message+" Use Sync now to try again." }
                : { kind: "ok", text: "iCloud account connected and contacts imported." });
            setStatus(data);
        }).catch(err => {
            if(!mounted.current){ return; }
            setBusy(false);
            setAppPassword("");   // never keep a rejected password around
            const errs = err && err.errors && typeof(err.errors) === "object" ? err.errors : {};
            setFieldErrors(errs);
            setNotice({ kind: "error", text: messageOf(err, "Your iCloud account could not be connected. Please try again.") });
            if(errs.app_specific_password && passwordRef.current){ passwordRef.current.focus(); }
        });
    };

    const disconnect = () => {
        setBusy(true);
        ICloudContactsApi.disconnect().then(data => {
            if(!mounted.current){ return; }
            setBusy(false);
            setConfirmDisconnect(false);
            setStatus(data);
            setSearch("");
            autoSynced.current = false;
            setNotice({ kind: "ok", text: "iCloud account disconnected. Sendlinx deleted the saved app-specific password and the imported contacts. Recipients you already selected stay selected." });
        }).catch(err => {
            if(!mounted.current){ return; }
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Your iCloud account could not be disconnected.") });
        });
    };

    const sortHeader = (key, label) => (
        <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button type="button" className={classes.sortButton} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
                {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
            </button>
        </th>
    );

    const header = (
        <div className={classes.msHeader}>
            <span className={classes.icTile} aria-hidden="true"><CloudOutlined /></span>
            <div>
                <h3 className={classes.msTitle}>iCloud Contacts</h3>
                <p className={classes.muted}>Apple iCloud, iCloud.com, @me.com &amp; @mac.com</p>
            </div>
        </div>
    );
    const noticeBox = notice ? (
        <div className={notice.kind === "error" ? classes.error : notice.kind === "ok" ? classes.success : classes.notice} role={notice.kind === "error" ? "alert" : "status"}>{notice.text}</div>
    ) : null;

    if(status === null){
        return <>{header}<p className={classes.empty}>Checking your iCloud connection…</p></>;
    }

    if(!connected){
        const reconnect = status.status === "reconnect";
        const notConfigured = status.configured === false;
        return (<>
            {header}
            {noticeBox}
            {statusError ? <div className={classes.error} role="alert">{statusError}</div> : null}
            <div className={classes.icPanel}>
                {notConfigured ? <>
                    <p><strong>iCloud contacts aren't available yet.</strong></p>
                    <p className={classes.muted}>Your Sendlinx administrator needs to turn on iCloud contacts. You can still type addresses or use your Sendlinx contacts.</p>
                </> : <form className={classes.icForm} onSubmit={connect} noValidate>
                    <div className={classes.icIntro}>
                        <span className={classes.icTileSmall} aria-hidden="true"><AccountBoxOutlined /></span>
                        <div>
                            <h4 className={classes.icConnectTitle}>{reconnect ? "Reconnect your iCloud account" : "Connect your iCloud account"}</h4>
                            <p className={classes.icLead}>Access your Apple iCloud contacts securely using an app-specific password.</p>
                        </div>
                    </div>
                    {reconnect ?
                        <p className={classes.msWarn} role="status"><ErrorOutline aria-hidden="true" />Apple no longer accepts the saved app-specific password{status.account && status.account.email ? " for "+status.account.email : ""}. It may have been revoked, or your Apple Account password was changed. Create a new app-specific password and reconnect.</p>
                    : null}
                    <div className={classes.icField}>
                        <label className={classes.label} htmlFor="icloud-apple-id">Apple Account email</label>
                        <input id="icloud-apple-id" ref={emailRef} type="email" className={classes.input+(fieldErrors.apple_account_email ? " "+classes.inputInvalid : "")}
                            placeholder="name@icloud.com, name@me.com, or the email you use for your Apple Account" autoComplete="username" spellCheck={false}
                            value={appleId} onChange={(e) => { setAppleId(e.target.value); setFieldErrors(f => ({ ...f, apple_account_email: null })); }}
                            aria-invalid={fieldErrors.apple_account_email ? "true" : "false"} aria-describedby={fieldErrors.apple_account_email ? "icloud-apple-id-error" : undefined}
                            disabled={busy} required />
                        {fieldErrors.apple_account_email ? <div className={classes.fieldError} id="icloud-apple-id-error" role="alert">{fieldErrors.apple_account_email}</div> : null}
                    </div>
                    <div className={classes.icField}>
                        <div className={classes.icLabelRow}>
                            <label className={classes.label} htmlFor="icloud-app-password" style={{ marginBottom: 0 }}>App-specific password</label>
                            <button type="button" className={classes.icInfoButton} aria-label="What is an app-specific password?" aria-expanded={showInfo}
                                aria-controls="icloud-app-password-info" onClick={() => setShowInfo(v => !v)}><InfoOutlined /></button>
                        </div>
                        {showInfo ?
                            <p className={classes.hint} id="icloud-app-password-info">A 16-letter password that Apple generates for one app, shown like abcd-efgh-ijkl-mnop. It lets Sendlinx read your contacts without your Apple Account password, and you can revoke it at any time.</p>
                        : null}
                        <div className={classes.icPasswordWrap}>
                            <input id="icloud-app-password" ref={passwordRef} type={showPassword ? "text" : "password"}
                                className={classes.input+" "+classes.icPasswordInput+(fieldErrors.app_specific_password ? " "+classes.inputInvalid : "")}
                                placeholder="Enter your app-specific password" autoComplete="new-password" spellCheck={false} autoCapitalize="off"
                                value={appPassword} onChange={(e) => { setAppPassword(e.target.value); setFieldErrors(f => ({ ...f, app_specific_password: null })); }}
                                aria-invalid={fieldErrors.app_specific_password ? "true" : "false"}
                                aria-describedby={["icloud-app-password-format", fieldErrors.app_specific_password ? "icloud-app-password-error" : null].filter(Boolean).join(" ")}
                                disabled={busy} required />
                            <button type="button" className={classes.icEye} onClick={() => setShowPassword(v => !v)} disabled={busy}
                                aria-label={showPassword ? "Hide app-specific password" : "Show app-specific password"} aria-pressed={showPassword}>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </button>
                        </div>
                        <span className={classes.srOnly} id="icloud-app-password-format">Format: xxxx-xxxx-xxxx-xxxx.</span>
                        {fieldErrors.app_specific_password ? <div className={classes.fieldError} id="icloud-app-password-error" role="alert">{fieldErrors.app_specific_password}</div> : null}
                    </div>
                    <button type="submit" className={classes.icConnectButton} disabled={busy} aria-busy={busy}>
                        <LinkIcon aria-hidden="true" />
                        {busy ? "Connecting and importing contacts…" : reconnect ? "Reconnect iCloud Account" : "Connect iCloud Account"}
                    </button>
                    <div className={classes.icHelp}>
                        <div className={classes.icHelpSteps}>
                            <EmojiObjectsOutlined className={classes.icBulb} aria-hidden="true" />
                            <div>
                                <p className={classes.icHelpTitle}>How to get an app-specific password</p>
                                <ol className={classes.icSteps}>{HELP_STEPS.map(step => <li key={step}>{step}</li>)}</ol>
                            </div>
                        </div>
                        <div className={classes.icHelpLink}>
                            <a href={APPLE_ACCOUNT_URL} target="_blank" rel="noopener noreferrer" className={classes.icAppleLink}>
                                <OpenInNew aria-hidden="true" />Open Apple Account<span className={classes.srOnly}> (opens in a new tab)</span>
                            </a>
                            <span className={classes.muted}>Generate an app-specific password for Sendlinx.</span>
                        </div>
                    </div>
                    <ul className={classes.msList}>
                        <li>Your main Apple Account password is not required.</li>
                        <li>Sendlinx imports and caches contact names and email addresses to support recipient selection. It never changes anything in iCloud.</li>
                        <li>You can revoke the app-specific password at any time in your Apple Account settings.</li>
                    </ul>
                </form>}
            </div>
        </>);
    }

    const list = rows || [];
    const allShown = list.length > 0 && list.every(r => isSelected(r.email));
    const filtered = search.trim() !== "";
    const synced = formatWhen(status.lastSyncedAt);
    const sync = status.sync || {};
    const syncProblem = sync.status === "error" && sync.error ? (SYNC_ERRORS[sync.error] || "The last sync didn't finish. Use Sync now to try again.")
        : sync.error === "truncated" ? SYNC_ERRORS.truncated : null;
    const running = syncing || sync.status === "running";
    return (<>
        {header}
        {noticeBox}
        <div className={classes.msConnected}>
            <CheckCircle className={classes.msOk} aria-hidden="true" />
            <span className={classes.msAccount}>Connected to <strong>{(status.account && status.account.email) || "your iCloud account"}</strong>
                <span className={classes.subLine}>{running ? "Syncing…" : synced ? "Last synced: "+synced : "Not synced yet"}
                    {!running && typeof sync.contactCount === "number" && synced ? " · "+sync.contactCount+" contact"+(sync.contactCount === 1 ? "" : "s") : ""}</span></span>
            <button type="button" className={classes.linkButton} onClick={() => runSync(false)} disabled={running || busy}
                aria-label="Sync iCloud contacts now"><Sync aria-hidden="true" />{running ? "Syncing…" : "Sync now"}</button>
            {confirmDisconnect ? <span className={classes.msConfirm} role="group" aria-label="Confirm disconnect">
                <span>Disconnect this account?</span>
                <button type="button" className={classes.danger} onClick={disconnect} disabled={busy}>{busy ? "Disconnecting…" : "Disconnect"}</button>
                <button type="button" className={classes.secondary} onClick={() => setConfirmDisconnect(false)} disabled={busy}>Keep</button>
            </span> :
            <button type="button" className={classes.msDisconnect} onClick={() => setConfirmDisconnect(true)}>Disconnect</button>}
        </div>
        {confirmDisconnect ?
            <p className={classes.hint}>Sendlinx deletes the saved app-specific password and the imported contacts. Nothing is deleted in iCloud. To revoke the password itself, remove it under App-Specific Passwords in your <a className={classes.inlineLink} href={APPLE_ACCOUNT_URL} target="_blank" rel="noopener noreferrer">Apple Account</a>.</p>
        : null}
        {syncProblem ? <div className={classes.notice} role="status">{syncProblem}</div> : null}
        <div className={classes.toolbar}>
            <input type="search" className={classes.search} placeholder="Search contacts by name, email or company…" aria-label="Search iCloud contacts"
                value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {listError ? <div className={classes.error} role="alert">{listError} <button type="button" className={classes.inlineLink} onClick={() => loadContacts(1)}>Try again</button></div> : null}
        <div className={classes.tableWrap} aria-busy={meta.loading}>
            <table className={classes.table}>
                <thead><tr>
                    <th scope="col" style={{ width: 44 }}>
                        <input type="checkbox" className={classes.checkbox} aria-label={"Select all "+list.length+" iCloud contacts shown"}
                            checked={allShown} disabled={list.length === 0} onChange={(e) => toggleMany(list, e.target.checked)} />
                    </th>
                    {sortHeader("name", "Name")}{sortHeader("email", "Email")}{sortHeader("company", "Company")}
                </tr></thead>
                <tbody>
                    {rows === null ? <tr><td colSpan={4} className={classes.empty}>Loading your iCloud contacts…</td></tr> :
                        list.length === 0 ? <tr><td colSpan={4} className={classes.empty}>{filtered ? "No iCloud contacts match." : running ? "Importing your iCloud contacts…" : "No contacts with an email address were found in your iCloud account."}</td></tr> :
                        list.map(r => (
                            <tr key={r.id} className={isSelected(r.email) ? classes.rowSelected : ""}>
                                <td><input type="checkbox" className={classes.checkbox} checked={isSelected(r.email)} onChange={() => toggle(r)}
                                    aria-label={"Select "+(r.name ? r.name+" ("+r.email+")" : r.email)} /></td>
                                <td>
                                    <span className={classes.nameCell}>
                                        <span className={classes.avatar} aria-hidden="true">{initials(r)}</span>
                                        <span>{r.name || <span className={classes.muted}>—</span>}{already.has(lower(r.email)) ? <span className={classes.muted}> (added)</span> : null}</span>
                                    </span>
                                </td>
                                <td>{r.email}</td>
                                <td>{r.company || <span className={classes.muted}>—</span>}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
        {rows !== null && list.length > 0 ?
            <div className={classes.toolbar}>
                <span className={classes.muted}>
                    Showing {list.length} of {meta.total}{filtered ? " matching" : ""}.
                    {allShown && meta.hasMore ? " All shown are selected; load more to select the rest." : " Select all applies to the rows shown."}
                </span>
                {meta.hasMore ?
                    <button type="button" className={classes.linkButton} disabled={meta.loading} onClick={() => loadContacts(meta.page + 1)}>
                        {meta.loading ? "Loading…" : "Load more"}
                    </button>
                : null}
            </div>
        : null}
    </>);
}

ICloudContactsView.propTypes = {
    classes: PropTypes.object.isRequired,
    isSelected: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    toggleMany: PropTypes.func.isRequired,
    already: PropTypes.object.isRequired
};

export default ICloudContactsView;
